// UltraFlex Admin — Access Control Device Management
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Cpu, Plus, Trash2, Edit3, Save, X, Wifi, WifiOff, Eye, EyeOff,
  Copy, Check, RefreshCw, MapPin, ArrowUpDown, Shield, AlertTriangle,
  Clock, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDevices = () => {
  const { token } = useAuth();
  const [devices, setDevices] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    device_name: '', site_id: '', entrance_id: 'main', device_type: 'raspberry_pi', direction: 'both', config: {}
  });
  const [newApiKey, setNewApiKey] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const [filterSite, setFilterSite] = useState('');
  const [error, setError] = useState(null);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterSite
        ? `${API_URL}/api/access/devices?site_id=${filterSite}`
        : `${API_URL}/api/access/devices`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setDevices(data.items || []);
    } catch { setDevices([]); }
    setLoading(false);
  }, [filterSite, token]);

  useEffect(() => {
    fetch(`${API_URL}/api/sites`, { headers })
      .then(r => r.json())
      .then(d => setSites(d.items || d || []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const resetForm = () => {
    setFormData({ device_name: '', site_id: '', entrance_id: 'main', device_type: 'raspberry_pi', direction: 'both', config: {} });
    setEditingId(null);
    setNewApiKey(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!formData.device_name || !formData.site_id) {
      setError('Name and site are required.');
      return;
    }

    try {
      if (editingId) {
        const res = await fetch(`${API_URL}/api/access/devices/${editingId}`, {
          method: 'PATCH', headers, body: JSON.stringify({
            device_name: formData.device_name, direction: formData.direction,
            entrance_id: formData.entrance_id, is_active: true
          })
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Update failed');
      } else {
        const res = await fetch(`${API_URL}/api/access/devices`, {
          method: 'POST', headers, body: JSON.stringify(formData)
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Create failed');
        const data = await res.json();
        if (data.api_key) setNewApiKey(data.api_key);
      }
      fetchDevices();
      if (editingId) { setShowForm(false); resetForm(); }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete device "${name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/api/access/devices/${id}`, { method: 'DELETE', headers });
      fetchDevices();
    } catch {}
  };

  const handleToggleActive = async (device) => {
    try {
      await fetch(`${API_URL}/api/access/devices/${device.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ is_active: !device.is_active })
      });
      fetchDevices();
    } catch {}
  };

  const startEdit = (device) => {
    setFormData({
      device_name: device.device_name, site_id: device.site_id,
      entrance_id: device.entrance_id || 'main',
      device_type: device.device_type, direction: device.direction,
      config: device.config || {}
    });
    setEditingId(device.id);
    setShowForm(true);
    setNewApiKey(null);
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const siteName = (id) => sites.find(s => s.id === id)?.name || id;

  const directionBadge = (dir) => {
    const m = { entry: 'bg-emerald-500/15 text-emerald-400', exit: 'bg-orange-500/15 text-orange-400', both: 'bg-blue-500/15 text-blue-400' };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${m[dir] || m.both}`}>{dir}</span>;
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-devices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-uf-text" data-testid="devices-page-title">Access Control Devices</h1>
          <p className="text-uf-text-muted text-sm mt-1">Provision and manage QR scanner hardware across your sites.</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          data-testid="add-device-btn"
          className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-uf-red/90 transition-colors"
        >
          <Plus className="w-4 h-4" />{showForm && !editingId ? 'Cancel' : 'Register Device'}
        </button>
      </div>

      {/* New API Key Banner */}
      {newApiKey && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5" data-testid="new-api-key-banner">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-emerald-400 font-semibold text-sm">Device Registered Successfully</p>
              <p className="text-uf-text-muted text-xs mt-1">
                Copy this API key now — it will <strong>not</strong> be shown again.
              </p>
              <div className="mt-3 flex items-center gap-2 bg-uf-surface rounded-lg p-3 border border-uf-border">
                <code className="text-uf-text text-xs font-mono flex-1 break-all">{newApiKey}</code>
                <button
                  onClick={() => copyKey(newApiKey)}
                  data-testid="copy-new-api-key-btn"
                  className="p-2 text-uf-text-muted hover:text-uf-text"
                >
                  {copiedKey === newApiKey ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <button
                onClick={() => { setNewApiKey(null); setShowForm(false); resetForm(); }}
                className="mt-3 text-xs text-uf-text-muted hover:text-uf-text"
              >
                Dismiss &amp; close form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration / Edit Form */}
      {showForm && !newApiKey && (
        <form onSubmit={handleSubmit} className="bg-uf-surface rounded-xl border border-uf-border p-5 space-y-4" data-testid="device-form">
          <h3 className="text-sm font-semibold text-uf-text">
            {editingId ? 'Edit Device' : 'Register New Scanner Device'}
          </h3>
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-uf-text-muted text-xs block mb-1">Device Name *</label>
              <input
                value={formData.device_name}
                onChange={e => setFormData({ ...formData, device_name: e.target.value })}
                placeholder="e.g. West Leeds — Main Entrance"
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
                data-testid="device-name-input"
                required
              />
            </div>
            <div>
              <label className="text-uf-text-muted text-xs block mb-1">Site *</label>
              <select
                value={formData.site_id}
                onChange={e => setFormData({ ...formData, site_id: e.target.value })}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
                data-testid="device-site-select"
                disabled={!!editingId}
                required
              >
                <option value="">Select site...</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-uf-text-muted text-xs block mb-1">Entrance ID</label>
              <input
                value={formData.entrance_id}
                onChange={e => setFormData({ ...formData, entrance_id: e.target.value })}
                placeholder="main"
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
                data-testid="device-entrance-input"
              />
            </div>
            <div>
              <label className="text-uf-text-muted text-xs block mb-1">Direction</label>
              <select
                value={formData.direction}
                onChange={e => setFormData({ ...formData, direction: e.target.value })}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
                data-testid="device-direction-select"
              >
                <option value="both">Both (Entry &amp; Exit)</option>
                <option value="entry">Entry Only</option>
                <option value="exit">Exit Only</option>
              </select>
            </div>
            {!editingId && (
              <div>
                <label className="text-uf-text-muted text-xs block mb-1">Device Type</label>
                <select
                  value={formData.device_type}
                  onChange={e => setFormData({ ...formData, device_type: e.target.value })}
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
                  data-testid="device-type-select"
                >
                  <option value="raspberry_pi">Raspberry Pi</option>
                  <option value="tablet">Tablet/Kiosk</option>
                  <option value="staff_mobile">Staff Mobile</option>
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); resetForm(); }}
              className="px-4 py-2 text-uf-text-muted text-sm hover:text-uf-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="submit-device-btn"
              className="flex items-center gap-2 bg-uf-red text-white px-5 py-2 rounded-lg text-sm font-semibold"
            >
              <Save className="w-4 h-4" />{editingId ? 'Update Device' : 'Register Device'}
            </button>
          </div>
        </form>
      )}

      {/* Filter by Site */}
      <div className="flex items-center gap-3">
        <MapPin className="w-4 h-4 text-uf-text-muted" />
        <select
          value={filterSite}
          onChange={e => setFilterSite(e.target.value)}
          className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
          data-testid="filter-site-select"
        >
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={fetchDevices} className="p-2 text-uf-text-muted hover:text-uf-text" data-testid="refresh-devices-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
        <span className="text-uf-text-muted text-xs ml-auto">{devices.length} device{devices.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Devices List */}
      {loading ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" />
        </div>
      ) : devices.length === 0 ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center" data-testid="no-devices">
          <Cpu className="w-12 h-12 text-uf-text-muted mx-auto mb-3 opacity-40" />
          <p className="text-uf-text-muted text-sm">No scanner devices registered yet.</p>
          <p className="text-uf-text-muted text-xs mt-1">Click "Register Device" to provision a new scanner.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="devices-list">
          {devices.map(device => (
            <div
              key={device.id}
              data-testid={`device-card-${device.id}`}
              className={`bg-uf-surface rounded-xl border p-5 transition-colors ${
                device.is_active ? 'border-uf-border' : 'border-red-500/20 opacity-60'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Status icon */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  device.is_active ? 'bg-emerald-500/15' : 'bg-red-500/15'
                }`}>
                  {device.is_active ? (
                    <Wifi className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <WifiOff className="w-5 h-5 text-red-400" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-uf-text font-semibold text-sm">{device.device_name}</h4>
                    {directionBadge(device.direction)}
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      device.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                    }`}>
                      {device.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-uf-text-muted text-xs flex-wrap">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{siteName(device.site_id)}</span>
                    <span>Entrance: {device.entrance_id || 'main'}</span>
                    <span className="capitalize">{device.device_type?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-uf-text-muted text-xs">
                    {device.last_seen_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />Last seen: {new Date(device.last_seen_at).toLocaleString('en-GB')}
                      </span>
                    )}
                    {device.last_ip && <span>IP: {device.last_ip}</span>}
                    {device.firmware_version && <span>FW: {device.firmware_version}</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(device)}
                    data-testid={`toggle-device-${device.id}`}
                    className="p-2 text-uf-text-muted hover:text-uf-text rounded-lg hover:bg-uf-surface-hi"
                    title={device.is_active ? 'Disable' : 'Enable'}
                  >
                    {device.is_active ? <WifiOff className="w-4 h-4" /> : <Wifi className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => startEdit(device)}
                    data-testid={`edit-device-${device.id}`}
                    className="p-2 text-uf-text-muted hover:text-uf-text rounded-lg hover:bg-uf-surface-hi"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(device.id, device.device_name)}
                    data-testid={`delete-device-${device.id}`}
                    className="p-2 text-red-400/60 hover:text-red-400 rounded-lg hover:bg-red-500/10"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDevices;
